import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Spin, Tabs, Tag, Typography, message } from 'antd';
import { ExportOutlined, SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@shared/components/PageHeader';
import { RichEditor } from '@shared/components/RichEditor';
import {
  EffectiveLegalPage,
  LegalPageType,
  useLegalPages,
  useUpsertLegalPage,
} from '@shared/api/hooks/useLegalPages';
import { env } from '@shared/runtimeEnv';

// Le pagine legali le serve il BACKEND (host API), non il BO: con un URL vuoto i
// link diventavano relativi e aprivano il BO stesso. In produzione l'host arriva
// dalla config runtime (PUBLIC_API_URL dell'istanza); in dev resta vuoto.
const PUBLIC_BASE = env('PUBLIC_BASE_URL').replace(/\/$/, '');

const TAB_META: Record<LegalPageType, { label: string; path: string; hint: string }> = {
  PRIVACY: {
    label: 'Privacy Policy',
    path: '/privacy',
    hint: 'Informativa GDPR dichiarata ad Apple App Store e Google Play.',
  },
  COOKIE: {
    label: 'Cookie Policy',
    path: '/cookie-policy',
    hint: 'Informativa cookie per le pagine web collegate.',
  },
  TERMS: {
    label: 'Termini e Condizioni',
    path: '/terms',
    hint: "Termini d'uso del servizio.",
  },
  REGULATION: {
    label: 'Regolamento Loyalty',
    path: '/regolamento',
    hint: 'Regolamento ufficiale del programma punti (omaggi, accumulo, check-in, controlli).',
  },
};

function PageEditor({ page }: { page: EffectiveLegalPage }): React.ReactElement {
  const meta = TAB_META[page.type];
  const upsert = useUpsertLegalPage();
  const [title, setTitle] = useState(page.title);
  const [body, setBody] = useState(page.body);

  // Re-sync quando cambia il contenuto effettivo (es. dopo salvataggio/refetch).
  useEffect(() => {
    setTitle(page.title);
    setBody(page.body);
  }, [page.title, page.body]);

  const dirty = title !== page.title || body !== page.body;
  const publicUrl = `${PUBLIC_BASE}${meta.path}`;

  const handleSave = async (): Promise<void> => {
    if (!title.trim() || !body.trim() || body === '<p><br></p>') {
      message.error('Titolo e contenuto sono obbligatori');
      return;
    }
    try {
      await upsert.mutateAsync({ type: page.type, input: { title: title.trim(), body } });
      message.success('Pagina salvata e pubblicata');
    } catch (err) {
      message.error(`Errore salvataggio: ${(err as Error).message}`);
    }
  };

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          {page.source === 'db' ? (
            <Tag color="green">Salvata · v{page.version}</Tag>
          ) : (
            <Tag color="default">Contenuto di default (non ancora personalizzato)</Tag>
          )}
          <Tag>Ultimo aggiornamento: {page.lastUpdated}</Tag>
          <Button
            type="link"
            size="small"
            icon={<ExportOutlined />}
            href={publicUrl}
            target="_blank"
            rel="noopener"
          >
            Apri pagina pubblica
          </Button>
        </Space>

        <Alert
          type="info"
          showIcon
          message={meta.hint}
          description={
            <Typography.Text copyable={{ text: publicUrl }} style={{ fontSize: 12 }}>
              URL pubblico da dichiarare allo store: <strong>{publicUrl}</strong>
            </Typography.Text>
          }
        />

        <Form layout="vertical">
          <Form.Item label="Titolo" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </Form.Item>
          <Form.Item label="Contenuto" required>
            <RichEditor value={body} onChange={(html) => setBody(html)} />
          </Form.Item>
        </Form>

        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={upsert.isPending}
            disabled={!dirty}
          >
            Salva e pubblica
          </Button>
          {dirty && <Typography.Text type="warning">Modifiche non salvate</Typography.Text>}
        </Space>
      </Space>
    </Card>
  );
}

export function LegalPagesPage(): React.ReactElement {
  const { data, isLoading, isError } = useLegalPages();

  const tabs = useMemo(
    () =>
      (data ?? []).map((page) => ({
        key: page.type,
        label: TAB_META[page.type].label,
        children: <PageEditor key={`${page.type}-${page.version}`} page={page} />,
      })),
    [data],
  );

  return (
    <div>
      <PageHeader
        breadcrumb="CONFIGURAZIONE"
        title="Pagine legali"
        subtitle="Privacy, Cookie e Termini — modificabili e pubblicati a URL pubblici per gli store"
      />
      <div style={{ padding: 24 }}>
        {isLoading && <Spin />}
        {isError && <Alert type="error" message="Errore nel caricamento delle pagine legali" />}
        {data && <Tabs items={tabs} />}
      </div>
    </div>
  );
}
